import {
  ARM,
  INSPECTION_X,
  PICK_X,
  STATIONS,
  capsuleX,
  gripAt,
} from './railLayout';

/**
 * ### Belt timing
 *
 * One loop carries a box from beyond the left edge of any screen to beyond
 * the right, with one stop on the way: every box halts under the microphone,
 * is lifted and turned on the plate there, and is set down again. Every box
 * shares the loop, offset by a negative delay so the belt is already
 * populated on first paint. The picker, the server's verdict and the plate
 * are all timed from the same numbers, so they stay in step with the boxes.
 * The loop is long enough that the belt rolls at a walking pace: the eye
 * has to be able to follow one unit from the plate to the picker.
 */
export const LOOP_SECONDS = 36;
export const TRAVEL_FROM = -1700;
export const TRAVEL_TO = 1400;

/**
 * Seven boxes to a cycle, read in the order they arrive at the station: two
 * good, one defect, three good, one defect. Box index 6 leads (its delay is
 * the most negative), so the arrival order is index 6 down to 0.
 */
export const BOX_COUNT = 7;
export const DEFECT_INDICES: ReadonlyArray<number> = [2, 6];

/**
 * ### PLATE_LIFT
 *
 * What the lift-and-turn station does with a unit, in seconds: raises it
 * clear of the belt, turns it one way for a full revolution, turns it back
 * the other way, and lowers it. The two turns are separate so each can
 * start and stop on its own; together with the lift they are the hold.
 */
export const PLATE_LIFT = {
  rise: 0.25,
  turn: 1,
  lower: 0.25,
} as const;

/**
 * ### INSPECTION_HOLD_SECONDS
 *
 * How long a unit stands at the inspection point in all: the whole of the
 * lift-and-turn cycle.
 */
export const INSPECTION_HOLD_SECONDS =
  PLATE_LIFT.rise + 2 * PLATE_LIFT.turn + PLATE_LIFT.lower;

const BOX_SPACING_SECONDS = LOOP_SECONDS / BOX_COUNT;

/**
 * The belt moves at one speed; the hold is time a box spends not moving.
 * So the distance of a loop is covered in the loop minus the hold, and a
 * box's speed is the same on both sides of the plate.
 */
const ROLLING_SECONDS = LOOP_SECONDS - INSPECTION_HOLD_SECONDS;
const UNITS_PER_SECOND = (TRAVEL_TO - TRAVEL_FROM) / ROLLING_SECONDS;
const INITIAL_LOOP_OFFSET_SECONDS = 1;

/** Animation delay of the box at `index`, negative so it starts mid-loop. */
export function boxDelaySeconds(index: number): number {
  return -index * BOX_SPACING_SECONDS - INITIAL_LOOP_OFFSET_SECONDS;
}

/**
 * ### localTimeAt
 *
 * Seconds into a box's own loop at which its centre reaches scene x. A
 * point past the plate is reached the hold later than rolling alone would
 * take, since the box stood still for that long on the way. At the plate
 * itself this is the moment of arrival; the box is still there a hold later.
 */
export function localTimeAt(x: number): number {
  const rolling = (x - TRAVEL_FROM) / UNITS_PER_SECOND;

  return x > INSPECTION_X ? rolling + INSPECTION_HOLD_SECONDS : rolling;
}

/**
 * ### InspectionWindow
 *
 * The moments of one hold, in the box's own time: arrival on the plate,
 * the plate fully up and the first turn starting, the turn reversing, the
 * plate starting down, and the box rolling on.
 */
export interface InspectionWindow {
  start: number;
  up: number;
  reverse: number;
  down: number;
  end: number;
}

/**
 * ### inspectionWindow
 *
 * The seconds into a box's own loop at which each stage of its hold
 * begins. Real times for a given box follow by adding its delay.
 *
 * @returns The hold's stages in the box's own time
 */
export function inspectionWindow(): InspectionWindow {
  const start = localTimeAt(INSPECTION_X);
  const up = start + PLATE_LIFT.rise;
  const reverse = up + PLATE_LIFT.turn;
  const down = reverse + PLATE_LIFT.turn;

  return { start, up, reverse, down, end: down + PLATE_LIFT.lower };
}

/**
 * Seconds into the shared loop at which the centre of the box at `index`
 * reaches scene x. A box with delay d is at loop-local time (t - d), so the
 * real time is the local time plus the (negative) delay, wrapped into the
 * loop.
 */
export function boxPassesAt(index: number, x: number): number {
  const real = localTimeAt(x) + boxDelaySeconds(index);

  return ((real % LOOP_SECONDS) + LOOP_SECONDS) % LOOP_SECONDS;
}

const [station] = STATIONS;

/**
 * For each defect box: when it arrives under the capsule, when the verdict
 * appears, and when it is under the gripper. Seconds into the loop.
 *
 * The verdict comes at the end of the hold, as the turn completes: the
 * server has heard the whole unit by then, and the badge, the plate's wash
 * and the picker's descent then follow one another in the order a viewer
 * expects a judgment to be acted on.
 */
export const DEFECT_EVENTS = DEFECT_INDICES.map((index) => {
  const heardAt = boxPassesAt(index, capsuleX(station));

  return {
    index,
    heardAt,
    judgedAt: (heardAt + INSPECTION_HOLD_SECONDS) % LOOP_SECONDS,
    pickedAt: boxPassesAt(index, PICK_X),
  };
});

/** How long the picker takes at each stage of a pick, in seconds. */
export const PICK = {
  descend: 0.7,
  hold: 0.45,
  lift: 0.9,
  release: 0.7,
} as const;

/**
 * How far the gripper travels between closing on a box and holding it clear
 * of the line. The held box is moved by exactly this, so it stays in the
 * gripper rather than drifting away from it.
 */
const grabbed = gripAt(ARM.grabAngle);
const raised = gripAt(ARM.liftAngle);
export const LIFT_OFFSET = {
  x: raised.x - grabbed.x,
  y: raised.y - grabbed.y,
} as const;

/** The verdict stays on the server screen this long before returning to OK. */
export const VERDICT_SECONDS = 1.6;
